import './quick-add.scss'
import ModalDialog from '../global/custom-elements/modal-dialog';
import VariantDropdown from '../variant-dropdown';

/**
 * SuperSubscriptions binds the lure dropdown with document.addEventListener('click').
 * That function has no guard, so a second init (new product, remounted script, or
 * shopify:section:load) stacks another listener: the first opens the menu and the
 * next immediately closes it. Keep only the first handler.
 */
function preventDuplicateSubscriptionDropdownListeners() {
    if (preventDuplicateSubscriptionDropdownListeners.patched) return;
    preventDuplicateSubscriptionDropdownListeners.patched = true;

    const original = document.addEventListener.bind(document);
    document.addEventListener = (type, listener, options) => {
        if (type === 'click' && typeof listener === 'function') {
            try {
                const source = Function.prototype.toString.call(listener);
                if (source.includes('sss-custom-dropdown-trigger')) {
                    if (preventDuplicateSubscriptionDropdownListeners.bound) return;
                    preventDuplicateSubscriptionDropdownListeners.bound = true;
                }
            } catch (error) {
                // If we cannot inspect the listener, fall through and attach it.
            }
        }
        return original(type, listener, options);
    };
}

preventDuplicateSubscriptionDropdownListeners();

export default class QuickAddModal extends ModalDialog {
    /**
     * Class constructor
     *
     * @arg {object} - `el` Selector for the media gallery
    */
    constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddModalContent"]');
        // Preserve mounted product DOM per URL so app blocks (e.g. SuperSubscriptions)
        // keep their directly-bound listeners across close/reopen.
        this.productContentCache = new Map();
        this.loadedProductUrl = null;
    }

    hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.parkCurrentContent();

        if (preventFocus) this.openedBy = null;
        super.hide();
    }

    /**
     * Move current drawer content into an in-memory cache without destroying it.
     * Some app blocks (e.g. SuperSubscriptions) bind change/click listeners directly
     * to nodes, so wiping innerHTML on close breaks selection on the next open.
     */
    parkCurrentContent() {
        if (!this.loadedProductUrl || !this.modalContent.firstChild) return;

        const node = this.modalContent.firstChild;
        this.modalContent.removeChild(node);
        this.productContentCache.set(this.loadedProductUrl, node);

        // Bound memory: keep only the most recent few products.
        if (this.productContentCache.size > 8) {
            const oldestKey = this.productContentCache.keys().next().value;
            this.productContentCache.delete(oldestKey);
        }
    }

    show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');

        // Reopen of a product we already mounted — restore cached DOM.
        const cached = this.productContentCache.get(productUrl);
        if (cached) {
            this.parkCurrentContent();
            this.modalContent.appendChild(cached);
            this.loadedProductUrl = productUrl;
            this.productContentCache.delete(productUrl);

            this.restoreSubscriptionSelection();
            super.show(opener);
            this.clearOpenerLoading(opener);
            return;
        }

        // Same product still sitting in the drawer.
        if (
            this.loadedProductUrl === productUrl &&
            this.modalContent.querySelector('.js-quick-add-product')
        ) {
            this.restoreSubscriptionSelection();
            super.show(opener);
            this.clearOpenerLoading(opener);
            return;
        }

        this.parkCurrentContent();

        fetch(productUrl)
            .then((response) => response.text())
            .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                this.productElement = responseHTML.querySelector('[id^="MainProduct-"]');
                this.preventDuplicatedIDs();
                this.removeDOMElements();
                this.adjustDOMElements();

                // Inject first so app scripts can find the block immediately.
                this.setInnerHTML(
                    this.modalContent,
                    `<div class="product js-quick-add-product">${this.productElement.innerHTML}</div>`
                );
                this.loadedProductUrl = productUrl;

                // App block assets (e.g. SuperSubscriptions) only ship on the PDP.
                // Load missing CSS onto collection/search. Do not remount SS JS —
                // the app embed already booted it, and a second boot double-binds
                // the selling-plan dropdown click handler (open then immediately close).
                return this.ensureProductAppAssets(responseHTML).then(() => {
                    if (window.Shopify && window.Shopify.PaymentButton) {
                        window.Shopify.PaymentButton.init();
                    }

                    if (window.ProductModel) window.ProductModel.loadShopifyXR();

                    this.removeGalleryListSemantic();
                    this.preventVariantURLSwitching();

                    this.reinitAppBlocks();

                    this.modalContent.querySelectorAll('.js-variant-dropdown-pdp').forEach((container) => {
                        new VariantDropdown(container);
                    });

                    return this.waitForSubscriptionWidget();
                }).then(() => {
                    super.show(opener);
                });
            })
            .finally(() => {
                // "Cached" code paths return before fetch runs, so only clear here.
                this.clearOpenerLoading(opener);
            });
    }

    ensureProductAppAssets(responseHTML) {
        const assetPromises = [];

        responseHTML.querySelectorAll('link[rel="stylesheet"][href*="cdn.shopify.com/extensions/"]').forEach((link) => {
            const href = link.getAttribute('href');
            if (!href || document.querySelector(`link[href="${href}"]`)) return;

            assetPromises.push(new Promise((resolve) => {
                const newLink = document.createElement('link');
                newLink.rel = 'stylesheet';
                newLink.href = href;
                newLink.type = 'text/css';
                newLink.media = 'all';
                newLink.onload = () => resolve();
                newLink.onerror = () => resolve();
                document.head.appendChild(newLink);
            }));
        });

        responseHTML.querySelectorAll('script[src*="cdn.shopify.com/extensions/"]').forEach((script) => {
            const src = script.getAttribute('src');
            if (!src) return;

            const existing = document.querySelector(`script[src*="${src.split('?')[0]}"]`);
            if (existing) return;

            assetPromises.push(new Promise((resolve) => {
                const newScript = document.createElement('script');
                newScript.src = src;
                newScript.defer = true;
                newScript.onload = () => resolve();
                newScript.onerror = () => resolve();
                document.head.appendChild(newScript);
            }));
        });

        return Promise.all(assetPromises);
    }

    /**
     * SuperSubscriptions watches for new widget markup with a 50ms debounce.
     * Wait for that pass so the selling-plan dropdown is clickable on first open.
     */
    waitForSubscriptionWidget() {
        if (!this.modalContent.querySelector('.sss-custom-dropdown, .super-simple-subs-wrapper')) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            window.setTimeout(resolve, 60);
        });
    }

    restoreSubscriptionSelection() {
        const root = this.modalContent;
        if (!root) return;

        const checked = root.querySelector(
            'input[name="purchase_option"]:checked, input[name="selling_plan_group"]:checked, .sss-subscription-toggle:checked'
        );
        if (checked) return;

        const fallback =
            root.querySelector('input[name="selling_plan_group"][value="one_time"]') ||
            root.querySelector('input[name="purchase_option"][value="one_time"]') ||
            root.querySelector('input[name="selling_plan_group"]') ||
            root.querySelector('input[name="purchase_option"]');

        if (!fallback) return;

        fallback.checked = true;
        fallback.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clearOpenerLoading(opener) {
        if (!opener) return;
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        const spinner = opener.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
    }

    /**
     * Re-initialise app blocks (e.g. SuperSubscriptions) after quick-add HTML injection.
     * App embeds often boot once on page load and need a nudge when markup is cloned into the modal.
     */
    reinitAppBlocks() {
        const appBlocks = this.modalContent.querySelectorAll('.js-quick-add-context-app-blocks');
        if (!appBlocks.length) return;

        document.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: this.productElement?.dataset?.section }
        }));

        // SuperSubscriptions and similar widgets that expose a global re-init hook
        const ss = window.SSSubscriptions || window.ssSubscriptions || window.SuperSubscriptions;
        if (ss && typeof ss.init === 'function') {
            ss.init();
        } else if (ss && typeof ss.reinit === 'function') {
            ss.reinit();
        }

        appBlocks.forEach((block) => {
            block.dispatchEvent(new CustomEvent('quick-add:app-blocks-ready', { bubbles: true }));
        });
    }

    setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach(oldScriptTag => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach(attribute => {
                newScriptTag.setAttribute(attribute.name, attribute.value)
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
    }

    preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
        const deleteDomElements = [
            'nav',
            'pickup-availability',
            'product-modal',
            '.swiper',
            '.swiper-scrollbar',
            '.js-quick-add-context-breadcrumb',
            '.js-product-accordion',
            '.js-product-media',
            '.js-quick-add-context-wysiwyg',
            '.js-quick-add-context-free-delivery-banner',
            '.js-quick-add-context-wtb-ctas',
            '.js-quick-add-context-cross-sells',
            '.js-quick-add-context-stages',
            '.js-product-kf-images',
            '.js-product-kf-icons',
            '.js-pdp-cta-sticky',
            '.js-quick-add-context-reviews',
            '.product-features',
            '.brand-icon',
        ];

        deleteDomElements.forEach(selector => {
            const elements = this.productElement.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(element => element.remove());
            }
        });

        this.keepOnlyQuickAddEssentials();
    }

    keepOnlyQuickAddEssentials() {
        const productInfo = this.productElement.querySelector('[id^="ProductInfo-"]');
        if (!productInfo) return;

        const keepSelectors = [
            '.js-quick-add-context-product-type',
            '.js-quick-add-context-heading',
            '.js-quick-add-context-price',
            '.js-quick-add-context-app-blocks',
            '.js-quick-add-context-variants',
            '.js-quick-add-context-quantity-atc'
        ];

        Array.from(productInfo.children).forEach(child => {
            const shouldKeep = keepSelectors.some(sel => child.matches(sel));
            if (!shouldKeep) child.remove();
        });
    }

    adjustDOMElements() {
        const wrap = this.productElement.querySelector('.js-quick-add-context-wrap');
        if (wrap) {
            wrap.classList.add('mediumMax:wrap', 'medium:px-10', 'large:px-[4.375rem]');
            wrap.classList.remove('medium:wrap');
        }

        const container = this.productElement.querySelector('.js-quick-add-context-container');
        if (container) {
            container.classList.add('py-12');
            container.classList.remove('container');
        }

        const productGrid = this.productElement.querySelector('.js-quick-add-context-grid');
        if (productGrid) productGrid.classList.remove('grid', 'grid-cols-4', 'medium:grid-cols-12', 'medium:gap-x-5');

        const modalMoreInfo = this.productElement.querySelector('.js-quick-add-more-info-cta, .product__modal-more-info');
        if (modalMoreInfo) {
            modalMoreInfo.classList.remove('hidden');
            modalMoreInfo.classList.add('inline-block', 'w-1/2', 'mr-2');
            const modalMoreInfoBtn = modalMoreInfo.querySelector('.button');
            if (modalMoreInfoBtn) {
                modalMoreInfoBtn.classList.add('w-full', 'h-[3.5625rem]');
            }
        }

        const vendorContent = this.productElement.querySelector('.js-quick-add-context-vendor');
        if (vendorContent) {
            vendorContent.classList.remove('hidden');
        }

        const productCarousel = this.productElement.querySelector('.js-pdp-media-gallery');
        if (productCarousel) productCarousel.classList.remove('js-pdp-media-gallery');

        const productInfoContainer = this.productElement.querySelector('.js-quick-add-context-product-info-container');
        if (productInfoContainer) {
            productInfoContainer.classList.remove('col-span-4', 'mediumMax:mt-10', 'mediumMax:wrap', 'medium:col-span-6', 'xLarge:medium:col-start-8', 'xLarge:medium-col-span-5');
            const productInfo = productInfoContainer.firstElementChild;
            if (productInfo) {
                productInfo.classList.remove('dodie', 'good-gout', 'inostime', 'biostime');
            }
        }

        const headingRoot = this.productElement.querySelector('.js-quick-add-context-heading');
        const h1 = headingRoot?.matches?.('h1') ? headingRoot : headingRoot?.querySelector('h1');
        if (h1) {
            h1.classList.add('body-l', 'w-full', 'font-Lato', 'mr-20', 'medium:mr-24');
            h1.classList.remove('heading-l', 'font-Colby');
        }

        const priceSwap = this.productElement.querySelector('.js-quick-add-context-price');
        if (priceSwap) {
            priceSwap.classList.remove('mt-5');
            priceSwap.classList.add('mt-[0.625rem]', 'items-baseline', 'medium:mt-1');
        }

        const appBlocks = this.productElement.querySelectorAll('.js-quick-add-context-app-blocks');
        appBlocks.forEach((appBlock) => {
            appBlock.classList.remove('mt-5');
            appBlock.classList.add('mt-4', 'medium:mt-5');
        });

        const variants = this.productElement.querySelector('.js-quick-add-context-variants');
        if (variants) {
            variants.classList.remove('xLarge:mt-8');
            variants.classList.add('medium:mt-[1.5625rem]');
        }

        const quantityAtc = this.productElement.querySelector('.js-quick-add-context-quantity-atc');
        if (quantityAtc) {
            quantityAtc.classList.remove('items-center', 'gap-2', 'mt-9', 'xLarge:gap-5', 'xLarge:mt-10');
            quantityAtc.classList.add('flex-wrap', 'gap-4', 'mt-10', 'medium:gap-5', 'medium:mt-8');
        }

        const quantityInput = this.productElement.querySelector('.js-quick-add-context-quantity-input');
        if (quantityInput) {
            quantityInput.classList.add('w-full');
            const input = quantityInput.querySelector('.quantity-input');
            if (input) {
                input.classList.add('quantity--mini', 'bg-transparent');
            }
        }

        const productForm = this.productElement.querySelector('.js-quick-add-context-product-form');
        if (productForm) {
            productForm.classList.remove('w-full');
            productForm.classList.add('inline-block', 'w-1/2', 'ml-2');
            const productFormError = productForm.querySelector('.product-form__error-message-wrapper');
            if (productFormError) {
                productFormError.classList.add('absolute', '-top-[3.75rem]', 'w-full', 'right-0', 'left-0', 'px-10', 'large:px-[4.375rem]');
            }
        }

        const submit = this.productElement.querySelector('.js-quick-add-context-submit');
        if (submit) {
            submit.classList.remove('button--full-width-mobile', 'button--dodie', 'button--good-gout', 'px-14');
            submit.classList.add('w-full', 'flex', 'justify-center', 'h-[3.5625rem]', 'px-5');
        }

        const btns = this.productElement.querySelector('.js-quick-add-context-btns');
        if (btns) {
            btns.classList.add('flex', 'w-full', 'absolute', 'bottom-0', 'left-0', 'px-10', 'pb-8', 'large:px-[4.375rem]');
        }
    }

    preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
        this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
        });
    }

    removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }
}
